import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { ThemeProvider } from '@mui/material';
import { LS_THEME_KEY } from '@shared/constants/storagesKeysConstants';
import { createTheme } from '@mui/material/styles';
import { themeSettings } from '@app/theme/themes';

export enum ThemeMode {
    LIGHT = 'light',
    DARK = 'dark',
    SYSTEM = 'system',
}

export enum CustomTheme {
    NEW_YEAR = 'new-year',
}

interface ColorModeContextType {
    themeMode: ThemeMode;
    setThemeMode: (mode: ThemeMode) => void;
    customTheme: CustomTheme | null;
    setCustomTheme: (theme: CustomTheme | null) => void;
    isCustomTheme: (theme: CustomTheme) => boolean;
}

const ColorModeContext = createContext<ColorModeContextType>({
    themeMode: ThemeMode.LIGHT,
    setThemeMode: () => {},
    customTheme: null,
    setCustomTheme: () => {},
    isCustomTheme: () => false,
});

interface ColorModeProviderProps {
    children: ReactNode;
}

const LS_CUSTOM_THEME_KEY = 'custom-theme';

const ColorModeProvider: FC<ColorModeProviderProps> = ({ children }) => {
    const [themeMode, setThemeModeState] = useState<ThemeMode>(
        (localStorage.getItem(LS_THEME_KEY) as ThemeMode) || ThemeMode.LIGHT,
    );

    const [customTheme, setCustomThemeState] = useState<CustomTheme | null>(() => {
        return null;
    });

    const getSystemTheme = useCallback((): ThemeMode.LIGHT | ThemeMode.DARK => {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return ThemeMode.DARK;
        }
        return ThemeMode.LIGHT;
    }, []);

    const getActiveTheme = useCallback((): ThemeMode.LIGHT | ThemeMode.DARK => {
        if (themeMode === ThemeMode.SYSTEM) {
            return getSystemTheme();
        }
        return themeMode as ThemeMode.LIGHT | ThemeMode.DARK;
    }, [themeMode, getSystemTheme]);

    const [activeTheme, setActiveTheme] = useState(getActiveTheme());

    useEffect(() => {
        if (customTheme) {
            localStorage.setItem(LS_CUSTOM_THEME_KEY, customTheme);
        } else {
            localStorage.setItem(LS_CUSTOM_THEME_KEY, '');
        }
    }, [customTheme]);

    useEffect(() => {
        const newActiveTheme = getActiveTheme();
        setActiveTheme(newActiveTheme);

        if (newActiveTheme === ThemeMode.DARK) {
            document.documentElement.setAttribute('theme', 'dark');
        } else {
            document.documentElement.removeAttribute('theme');
        }

        localStorage.setItem(LS_THEME_KEY, themeMode);
    }, [themeMode, getActiveTheme]);

    useEffect(() => {
        Object.values(CustomTheme).forEach((theme) => {
            document.documentElement.removeAttribute(`data-theme-${theme}`);
        });

        if (customTheme) {
            document.documentElement.setAttribute(`data-theme-${customTheme}`, 'true');
        }
    }, [customTheme]);

    useEffect(() => {
        if (themeMode !== ThemeMode.SYSTEM) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = () => {
            const newActiveTheme = getActiveTheme();
            setActiveTheme(newActiveTheme);

            if (newActiveTheme === ThemeMode.DARK) {
                document.documentElement.setAttribute('theme', 'dark');
            } else {
                document.documentElement.removeAttribute('theme');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [themeMode, getActiveTheme]);

    const setThemeMode = useCallback((mode: ThemeMode) => {
        setThemeModeState(mode);
    }, []);

    const setCustomTheme = useCallback((theme: CustomTheme | null) => {
        setCustomThemeState(theme);
    }, []);

    const isCustomTheme = useCallback((theme: CustomTheme) => customTheme === theme, [customTheme]);

    const contextValue = useMemo(
        () => ({
            themeMode,
            setThemeMode,
            customTheme,
            setCustomTheme,
            isCustomTheme,
        }),
        [themeMode, setThemeMode, customTheme, setCustomTheme, isCustomTheme],
    );

    const theme = useMemo(() => createTheme(themeSettings(activeTheme)), [activeTheme]);

    return (
        <ColorModeContext.Provider value={contextValue}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export const useColorModeContext = () => {
    const ctx = useContext(ColorModeContext);
    if (!ctx) throw new Error('useColorModeContext must be used inside ColorModeProvider');
    return ctx;
};

export default ColorModeProvider;
